// Money DISPLAY formatting only. CLAUDE.md: "Money is DECIMAL(12,2). Never
// float. Never JS arithmetic on prices without rounding at the boundary."
// The authoritative subtotal/vat/total are always computed server-side
// (repositories/quotationRepository.js's SQL SUM/ROUND) — nothing here is
// ever sent back to the API. The one exception is the pricing workspace's
// on-screen "preview" running total while a quotation is still a draft
// (before the server has computed real totals) — that preview uses the
// integer-cents helpers below specifically so it never touches JS floating
// point, even though it's just for display, not because the API needs it.
//
// mysql2 returns DECIMAL columns as strings (no `decimalNumbers` option is
// set on backend/src/db/pool.js), so every value this module receives from
// an API response is already a decimal string like "1234.50" — formatting
// must not round-trip that through a float and back.

// Splits a decimal string/number into integer cents (a BigInt) without ever
// constructing a JS float from the fractional part. Accepts up to 2 decimal
// places (unit_price/line_total/subtotal/vat_amount/total are all
// DECIMAL(12,2)); anything with more is truncated defensively rather than
// thrown, since this is a display path, not a validation path (validation
// already happened server-side via shared/schemas/pricingSchemas.js's
// mirrored pattern).
export function toCents(value) {
  if (value === null || value === undefined || value === '') return 0n;
  const str = String(value).trim();
  const negative = str.startsWith('-');
  const unsigned = negative ? str.slice(1) : str;
  const [wholePart, fracPartRaw = ''] = unsigned.split('.');
  const fracPart = (fracPartRaw + '00').slice(0, 2);
  const whole = wholePart === '' ? 0n : BigInt(wholePart);
  const cents = whole * 100n + BigInt(fracPart || '0');
  return negative ? -cents : cents;
}

export function centsToDecimalString(cents) {
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const wholePart = abs / 100n;
  const fracPart = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${wholePart.toString()}.${fracPart}`;
}

// qty is DECIMAL(12,3) — kept as a plain JS number for the multiply since a
// *preview* line total only needs to be "close enough to what the server
// will compute" for the sales user's own sanity check, never authoritative
// (see this file's header comment). Rounds to the nearest cent, matching
// the server's own `ROUND(qty * unit_price, 2)`.
export function previewLineTotalCents(qty, unitPrice) {
  const qtyNum = Number(qty);
  const unitPriceCents = toCents(unitPrice);
  if (!Number.isFinite(qtyNum)) return 0n;
  return BigInt(Math.round(Number(unitPriceCents) * qtyNum));
}

export function sumCents(centsList) {
  return centsList.reduce((acc, c) => acc + c, 0n);
}

// Groups an unsigned decimal string's whole part with thousands separators
// (plain comma grouping — Intl.NumberFormat is deliberately not used here so
// grouping never risks a float round-trip on a DECIMAL(12,2) value that can
// exceed Number.MAX_SAFE_INTEGER's safe-precision digit budget once VAT/
// large BOQ subtotals are involved).
function groupThousands(digits) {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// The one function every screen uses to print a money value. Always shows
// exactly 2 decimal places. Callers apply `font-variant-numeric:
// tabular-nums` via CSS (root CLAUDE.md) — this function only returns the
// digit string, it does not touch styling.
export function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const cents = toCents(value);
  const negative = cents < 0n;
  const abs = negative ? -cents : cents;
  const wholePart = (abs / 100n).toString();
  const fracPart = (abs % 100n).toString().padStart(2, '0');
  return `${negative ? '-' : ''}${groupThousands(wholePart)}.${fracPart}`;
}

export function formatMoneyWithCurrency(value, currency = 'ETB') {
  return `${formatMoney(value)} ${currency}`;
}
